/**
 * Auth Context (Keycloak SSO)
 *
 * Reflects the server-side login state held by the PHP BFF (HttpOnly TRUSSUSER
 * cookie). The browser never sees tokens.
 *
 *   On mount: GET /api/auth-session.php to hydrate.
 *   login():  redirect to /api/auth-login.php (Keycloak handles the rest).
 *   logout(): DELETE /api/auth-session.php, then optionally bounce to Keycloak
 *             RP-initiated logout to end the IdP SSO session.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { fetchUserProfile, UserProfile, resetAuthCsrf, nativeLogin, nativeSetPassword, nativeVerifyCode, nativeResetComplete } from '../services/api';
import { tr } from '../i18n/standalone';
import { useAppSettings } from './AppSettingsContext';
import { getRetailerAccountsEnabled } from '../data/productTable';

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * A proposed or confirmed link between the user and a retailer
 * (retailer_members). `pending` / `unmatched` await Trusscore approval;
 * `retailerId` is null while the admin still has to pick the retailer.
 */
export interface RetailerMembership {
  retailerId: number | null;
  retailerName: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'unmatched';
  role: string;
  /** Approved member: may open /retaileradmin. Absent on older server builds. */
  canView?: boolean;
  /** Approved manager: edit estimates, approve colleagues. */
  canManage?: boolean;
  allLocations?: boolean;
  locations?: { id: number; name: string; city: string | null }[];
  source: string;
  createdAt: string | null;
}

export interface AuthUser {
  sub: string;
  email: string | null;
  emailVerified: boolean;
  name: string | null;
  roles: string[];
  retailerId: string | null;
  retailerName: string | null;
  /** Absent on older server builds; treat as []. */
  retailerMemberships?: RetailerMembership[];
  /** The retailer a signed-in retailer user prices with on the main site (null = none / Trusscore). */
  activeRetailerId?: number | null;
  /** Where this person may save an estimate FOR a customer; null = cannot. [] = anywhere (Trusscore staff). */
  prepareLocations?: { id: number; name: string; city: string | null; retailerId: number; retailerName: string }[] | null;
  /** Partner locations (retailer stores / installers) this account is attached to — shows the "Shared with us" tab. */
  partnerLocations?: { id: number; name: string; kind: 'retailer' | 'installer' }[];
}

/**
 * A transient auth confirmation to flash, as an i18n key plus its values.
 * Deliberately not a rendered sentence — see flashSignedIn below.
 */
export interface AuthToastSignal {
  key: string;
  params?: Record<string, string | number>;
  /** Bumped per toast so a repeat of the same message still re-triggers. */
  id: number;
  variant: 'success' | 'danger';
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: AuthUser | null;
  /** True while the initial session probe is in flight. */
  isChecking: boolean;
  /** The user's saved profile (phone, I-Am-A, default location). Null until loaded. */
  profile: UserProfile | null;
  /** Coarse signal: profile has an "I Am A" + country. Region-if-needed is checked at point of use. */
  profileComplete: boolean;
  /** Re-fetch the profile from the server (after a save). */
  refreshProfile: () => Promise<void>;
  /** Re-read the server session (roles, retailer memberships) without a reload — e.g. after an approval. */
  refreshSession: () => Promise<void>;
  /** Whether the user holds a given Keycloak realm role. */
  hasRole: (role: string) => boolean;
  /** Redirect to Keycloak login; returns to `returnTo` (defaults to current path). */
  login: (returnTo?: string) => void;
  /**
   * Redirect to Keycloak's self-service registration page. Same OIDC flow as
   * login(); after the user registers and verifies their email they land back
   * at `returnTo` (defaults to the account workspace with a first-run welcome).
   */
  signup: (returnTo?: string) => void;
  /**
   * Clear the session and end the Keycloak SSO session. In native mode this
   * opens an in-app confirmation modal first (no Keycloak "log out?" page);
   * otherwise it logs out immediately.
   */
  logout: () => Promise<void>;
  /** Native flow: whether the in-app logout-confirmation modal is open. */
  logoutModalOpen: boolean;
  /** Native flow: proceed with logout (from the confirmation modal). */
  confirmLogout: () => Promise<void>;
  /** Native flow: dismiss the logout-confirmation modal. */
  cancelLogout: () => void;
  /** Transient toast trigger — set after a native login (success) or logout (danger). */
  authToast: AuthToastSignal | null;
  /** Native flow: whether the in-app auth modal is open. */
  authModalOpen: boolean;
  /** Native flow: which view the modal should open on ('login' | 'signup'). */
  authModalView: 'login' | 'signup';
  /** Native flow: email to pre-fill the modal with (e.g. from a ?signin deep link). */
  authModalEmail: string | undefined;
  /** Open the native auth modal (login()/signup() do this automatically in native mode). */
  openAuthModal: (returnTo?: string, view?: 'login' | 'signup', email?: string) => void;
  /** Close the native auth modal. */
  closeAuthModal: () => void;
  /**
   * Native password login (ROPC via the BFF). On success, sets the user and
   * closes the modal. On a fallback response, redirects to the hosted flow.
   * When `passwordChangeRequired` is set, the credentials were valid but a
   * forced password change is pending — the modal should switch to its
   * set-password view. Resolves with { ok, message } so it can show inline errors.
   */
  loginWithPassword: (email: string, password: string) => Promise<{
    ok: boolean;
    message?: string;
    /** Forced password change pending — show the set-password view. */
    passwordChangeRequired?: boolean;
    /** Email unverified — a code was emailed; show the code view. */
    emailVerification?: boolean;
    /** Seconds the emailed code stays valid (with emailVerification). */
    expiresIn?: number;
  }>;
  /**
   * Native forced-password change (finishes a temp-password login in-app). On
   * success, sets the user and closes the modal; on fallback, redirects to the
   * hosted flow. Resolves with { ok, message } for inline errors.
   */
  completePasswordChange: (email: string, currentPassword: string, newPassword: string) => Promise<{ ok: boolean; message?: string }>;
  /**
   * Native signup code flow: submit the emailed code. On success the server
   * creates + verifies the account and logs the user in (sets the user, closes
   * the modal). Otherwise resolves with the error (incorrect/expired/too_many)
   * and any remaining attempts.
   */
  verifySignupCode: (code: string) => Promise<{ ok: boolean; message?: string; error?: string; attemptsLeft?: number }>;
  /**
   * Native password reset: set the new password after the code is verified. On
   * success the server logs the user in (sets the user, closes the modal, flashes
   * the toast). Otherwise resolves with the error.
   */
  completePasswordReset: (newPassword: string) => Promise<{ ok: boolean; message?: string; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const { settings } = useAppSettings();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalView, setAuthModalView] = useState<'login' | 'signup'>('login');
  const [authModalEmail, setAuthModalEmail] = useState<string | undefined>(undefined);
  const [pendingReturnTo, setPendingReturnTo] = useState<string | undefined>(undefined);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [authToast, setAuthToast] = useState<AuthToastSignal | null>(null);
  const toastId = useRef(0);

  // Flash a "signed in" confirmation after a native (modal-driven) login. Not
  // fired on boot session-hydration — only on an explicit in-app sign-in.
  //
  // Carries an i18n key rather than a sentence: this context runs the auth state
  // machine and has no business composing display text. AuthToast translates it.
  const flashSignedIn = useCallback((u: AuthUser) => {
    const who = (u.name && u.name.trim()) || u.email || '';
    toastId.current += 1;
    setAuthToast({
      key: who ? 'auth:toast.signedInAs' : 'auth:toast.signedIn',
      params: who ? { who } : undefined,
      id: toastId.current,
      variant: 'success',
    });
  }, []);

  // Flash a "signed out" confirmation when logout completes in-app (no redirect).
  const flashSignedOut = useCallback(() => {
    toastId.current += 1;
    setAuthToast({ key: 'auth:toast.signedOut', id: toastId.current, variant: 'danger' });
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      setProfile(await fetchUserProfile());
    } catch {
      /* keep last-known profile on transient errors */
    }
  }, []);

  // Load the profile once the user is authenticated; clear it on logout.
  useEffect(() => {
    if (user) {
      refreshProfile();
    } else {
      setProfile(null);
    }
  }, [user, refreshProfile]);

  // The session probe: on mount, and again on demand (refreshSession) when
  // something server-side may have changed what the user is — a retailer
  // approval, a role change — so the menu and gates catch up without a reload.
  const probeSession = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth-session.php`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUser(data.authenticated ? (data.user as AuthUser) : null);
      }
    } catch {
      // Network error → keep the last-known state; on mount that means logged out.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    probeSession().finally(() => { if (!cancelled) setIsChecking(false); });
    return () => { cancelled = true; };
  }, [probeSession]);

  const hostedLogin = useCallback((returnTo?: string) => {
    const dest = returnTo ?? (window.location.pathname + window.location.search);
    window.location.assign(`${API_URL}/api/auth-login.php?return_to=${encodeURIComponent(dest)}`);
  }, []);

  const openAuthModal = useCallback((returnTo?: string, view: 'login' | 'signup' = 'login', email?: string) => {
    setPendingReturnTo(returnTo ?? (window.location.pathname + window.location.search));
    setAuthModalView(view);
    setAuthModalEmail(email);
    setAuthModalOpen(true);
  }, []);

  // Deep link: emailed verify/reset links return to `<origin>/?signin=1[&email=]`
  // so the app finishes sign-in in-place (native modal, pre-filled) rather than
  // bouncing to Keycloak's hosted login. Runs once the session probe settles.
  useEffect(() => {
    if (isChecking) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('signin') !== '1') return;

    const email = params.get('email') || undefined;
    // Strip the params so a refresh/back doesn't re-trigger the prompt.
    params.delete('signin');
    params.delete('email');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);

    if (user) return; // already signed in — nothing to do
    if (settings.accountsEnabled && settings.nativeAuthEnabled) openAuthModal(undefined, 'login', email);
  }, [isChecking, user, settings.accountsEnabled, settings.nativeAuthEnabled, openAuthModal]);

  // A hosted-flow login (OIDC callback: register-verify, social, reset link,
  // hosted password) lands back with `?signedin=1`. Flash the "signed in" toast
  // once the session has hydrated, then strip the param so a refresh won't repeat it.
  useEffect(() => {
    if (isChecking) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('signedin') !== '1') return;
    params.delete('signedin');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
    if (user) flashSignedIn(user);
  }, [isChecking, user, flashSignedIn]);

  const closeAuthModal = useCallback(() => setAuthModalOpen(false), []);

  // Native mode opens the in-app modal; otherwise redirect to Keycloak's pages.
  // No-op when the accounts system is off (defensive — the sign-in UI is hidden).
  const login = useCallback((returnTo?: string) => {
    if (!settings.accountsEnabled) return;
    if (settings.nativeAuthEnabled) {
      openAuthModal(returnTo);
    } else {
      hostedLogin(returnTo);
    }
  }, [settings.accountsEnabled, settings.nativeAuthEnabled, openAuthModal, hostedLogin]);

  const loginWithPassword = useCallback(
    async (
      email: string,
      password: string,
    ): Promise<{
      ok: boolean;
      message?: string;
      passwordChangeRequired?: boolean;
      emailVerification?: boolean;
      expiresIn?: number;
    }> => {
      try {
        const data = await nativeLogin(email, password);
        if (data.authenticated && data.user) {
          const u = data.user as AuthUser;
          setUser(u);
          flashSignedIn(u);
          setAuthModalOpen(false);
          return { ok: true };
        }
        if (data.passwordChangeRequired) {
          // Valid credentials, forced password change pending → handle in-app.
          return { ok: false, passwordChangeRequired: true };
        }
        if (data.emailVerification) {
          // Credentials were right but the address was never confirmed. The server
          // has emailed a code — stay in the modal and collect it, exactly as in
          // signup. (Keycloak has no mailer, so bouncing to it would strand them.)
          return { ok: false, emailVerification: true, expiresIn: data.expiresIn };
        }
        if (data.fallback) {
          // An interactive Keycloak required action we can't drive in-app (never a
          // mail-dependent one) → finish on the hosted page.
          hostedLogin(pendingReturnTo);
          return { ok: false, message: tr('auth:errors.redirecting') };
        }
        return { ok: false, message: data.message || tr('auth:errors.signInFailed') };
      } catch {
        return { ok: false, message: tr('auth:errors.generic') };
      }
    },
    [hostedLogin, pendingReturnTo, flashSignedIn],
  );

  const completePasswordChange = useCallback(
    async (email: string, currentPassword: string, newPassword: string): Promise<{ ok: boolean; message?: string }> => {
      try {
        const data = await nativeSetPassword(email, currentPassword, newPassword);
        if (data.authenticated && data.user) {
          const u = data.user as AuthUser;
          setUser(u);
          flashSignedIn(u);
          setAuthModalOpen(false);
          return { ok: true };
        }
        if (data.fallback) {
          // Password may have been changed already; finish sign-in on the hosted page.
          hostedLogin(pendingReturnTo);
          return {
            ok: false,
            // This context has no hook, so it uses the standalone accessor.
            message: tr(data.passwordChanged ? 'auth:errors.passwordUpdatedRedirect' : 'auth:errors.redirecting'),
          };
        }
        return { ok: false, message: data.message || tr('auth:errors.passwordUpdateFailed') };
      } catch {
        return { ok: false, message: tr('auth:errors.generic') };
      }
    },
    [hostedLogin, pendingReturnTo, flashSignedIn],
  );

  const verifySignupCode = useCallback(
    async (code: string): Promise<{ ok: boolean; message?: string; error?: string; attemptsLeft?: number }> => {
      try {
        const data = await nativeVerifyCode(code);
        if (data.authenticated && data.user) {
          const u = data.user as AuthUser;
          setUser(u);
          flashSignedIn(u);
          setAuthModalOpen(false);
          return { ok: true };
        }
        return { ok: false, error: data.error, message: data.message, attemptsLeft: data.attemptsLeft };
      } catch {
        return { ok: false, message: tr('auth:errors.generic') };
      }
    },
    [flashSignedIn],
  );

  const completePasswordReset = useCallback(
    async (newPassword: string): Promise<{ ok: boolean; message?: string; error?: string }> => {
      try {
        const data = await nativeResetComplete(newPassword);
        if (data.authenticated && data.user) {
          const u = data.user as AuthUser;
          setUser(u);
          flashSignedIn(u);
          setAuthModalOpen(false);
          return { ok: true };
        }
        return { ok: false, error: data.error, message: data.message };
      } catch {
        return { ok: false, message: tr('auth:errors.generic') };
      }
    },
    [flashSignedIn],
  );

  const hostedSignup = useCallback((returnTo?: string) => {
    // New registrants land in their own workspace with a first-run welcome.
    const dest = returnTo ?? '/account?welcome=1';
    window.location.assign(
      `${API_URL}/api/auth-login.php?flow=register&return_to=${encodeURIComponent(dest)}`,
    );
  }, []);

  // Native mode opens the in-app modal on the signup view; otherwise hosted.
  // No-op when the accounts system is off (defensive — the sign-up UI is hidden).
  const signup = useCallback((returnTo?: string) => {
    if (!settings.accountsEnabled) return;
    if (settings.nativeAuthEnabled) {
      openAuthModal(returnTo, 'signup');
    } else {
      hostedSignup(returnTo);
    }
  }, [settings.accountsEnabled, settings.nativeAuthEnabled, openAuthModal, hostedSignup]);

  const performLogout = useCallback(async () => {
    let endSessionUrl: string | null = null;
    let idpSessionEnded = false;
    try {
      const res = await fetch(`${API_URL}/api/auth-session.php`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        endSessionUrl = data.endSessionUrl ?? null;
        idpSessionEnded = !!data.idpSessionEnded;
      }
    } catch {
      /* ignore — clear local state regardless */
    }
    // The PHP session (and the CSRF token bound to it) is gone — drop the cached
    // token so the next session fetches its own instead of sending a dead one.
    resetAuthCsrf();
    setUser(null);
    setLogoutModalOpen(false);
    // Only bounce to Keycloak's front-channel logout when we couldn't end the SSO
    // session back-channel — otherwise logout stays fully in-app (no redirect),
    // so show the confirmation toast instead.
    if (!idpSessionEnded && endSessionUrl) {
      window.location.assign(endSessionUrl);
    } else {
      flashSignedOut();
    }
  }, [flashSignedOut]);

  // Native mode confirms in-app first; hosted mode logs out immediately.
  const logout = useCallback(async () => {
    if (settings.nativeAuthEnabled) {
      setLogoutModalOpen(true);
      return;
    }
    await performLogout();
  }, [settings.nativeAuthEnabled, performLogout]);

  const cancelLogout = useCallback(() => setLogoutModalOpen(false), []);

  const hasRole = useCallback(
    (role: string) => !!user && user.roles.includes(role),
    [user],
  );

  const profileComplete = !!(profile && profile.userCategory.trim() && profile.country.trim());

  // Master accounts switch. When OFF, the whole end-user auth surface reads as
  // logged-out — the guest experience — regardless of any leftover session. We
  // mask at this single seam so consuming components (Header, EstimateResults,
  // RequireAuth) need no per-site changes; only the logged-out ENTRY points
  // (sign-in buttons, /account routes) need their own explicit gate, since those
  // render precisely when logged-out. Internal `user` state is untouched, so the
  // moment the toggle flips on the real identity reappears without a reload.
  //
  // A retailer can additionally suppress accounts on their own portal even when
  // the global switch is on — some partners want a pure guest experience and no
  // sign-in surface at all. This never blocks anyone from claiming their guest
  // estimates later: claiming happens on whatever portal they DO sign up on, and
  // matches on the verified email alone (auth-claim-estimates.php).
  //
  // Safe to read from the module here rather than a context: AuthProvider is
  // mounted inside ProductsBootGate, which blocks rendering until the price book
  // (and this flag with it) has loaded, and unmounts the subtree when the
  // retailer changes — so this cannot be read stale.
  const accountsOn = settings.accountsEnabled && getRetailerAccountsEnabled();
  const effUser = accountsOn ? user : null;
  const gatedHasRole = useCallback(
    (role: string) => accountsOn && hasRole(role),
    [accountsOn, hasRole],
  );

  const value = useMemo<AuthContextType>(
    () => ({
      isAuthenticated: accountsOn && !!user,
      user: effUser,
      isChecking,
      profile,
      profileComplete,
      refreshProfile,
      refreshSession: probeSession,
      hasRole: gatedHasRole,
      login,
      signup,
      logout,
      logoutModalOpen,
      confirmLogout: performLogout,
      cancelLogout,
      authToast,
      authModalOpen,
      authModalView,
      authModalEmail,
      openAuthModal,
      closeAuthModal,
      loginWithPassword,
      completePasswordChange,
      verifySignupCode,
      completePasswordReset,
    }),
    [accountsOn, effUser, isChecking, profile, profileComplete, refreshProfile, probeSession, gatedHasRole, login, signup, logout,
     logoutModalOpen, performLogout, cancelLogout, authToast,
     authModalOpen, authModalView, authModalEmail, openAuthModal, closeAuthModal, loginWithPassword, completePasswordChange, verifySignupCode, completePasswordReset],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
